import {
    Button,
    CloseIcon,
    Datepicker,
    pxToRem,
    Flex,
    Text,
    ComponentEventHandler,
    DatepickerProps,
    ShorthandValue,
    InputProps
  } from '@fluentui/react-northstar';
  import React, { useState } from 'react';
import i18next from 'i18next';
import { customDateFormatter } from '../../../tikit/ticketHelper';
import { DatepickerLocalizations } from '../../../App';

  

/** The calendar's own render with a Clear button under it - a Datepicker's `calendar.children` for a date that is set. */
export const calendarWithClear = (onClear: () => void) =>
  (Component: React.ElementType, props: Record<string, unknown>) => (
    <>
      <Component {...props} />
      <Flex hAlign="end">
        <Button size="small" text icon={<CloseIcon size="small" />} content={i18next.t('time-tracking.clear')} onClick={onClear} />
      </Flex>
    </>
  );

export const TicketDatePicker = (isDisabled:boolean, selectedDate?: Date,
    input?: ShorthandValue<InputProps>,
    onDateChange?: ComponentEventHandler<DatepickerProps & {
        value: Date;
      }>,
    // Optional and undefined by default, so existing callers (Due Date, Resolution
    // Date) keep allowing future dates. Only fields that must not accept the future
    // pass this.
    maxDate?: Date,
    // Given for a date that may be empty (Due Date, Resolution Date, an optional date custom property): a Clear
    // button under the calendar, as the ticket list's date pop-up has (Shaun, 2026-10-19). Left out, nothing changes.
    onClear?: () => void,
    // Added to the picker's own class - Add Ticket's grey filled box (fluidDate + inverted).
    className?: string ) =>
  <TicketDatePickerBox {...{ isDisabled, selectedDate, input, onDateChange, maxDate, onClear, className }} />;

const TicketDatePickerBox = ({ isDisabled, selectedDate, input, onDateChange, maxDate, onClear, className }: {
    isDisabled: boolean;
    selectedDate?: Date;
    input?: ShorthandValue<InputProps>;
    onDateChange?: ComponentEventHandler<DatepickerProps & { value: Date }>;
    maxDate?: Date;
    onClear?: () => void;
    className?: string;
  }) => {
    // Bumped by Clear: the picker is drawn again, which closes its calendar and drops the date it held - Northstar's
    // Datepicker has no way to close its calendar from outside, and keeps its own date when given none.
    const [instance, setInstance] = useState(0);


    const calendarChildren = (ComponentType, props) => {
      return (
        <Flex space="between" hAlign="center">
          <Datepicker.CalendarHeaderAction
            onClick={props.onPreviousClick}
            direction="previous"
            title={props.prevMonthAriaLabel}
            disabled={props.disabledPreviousButton}
          />
          <Text
            content={props.label}
            styles={{
              paddingTop: pxToRem(5)
            }}
          />
          <Datepicker.CalendarHeaderAction
            onClick={props.onNextClick}
            direction="next"
            title={props.nextMonthAriaLabel}
            disabled={props.disabledNextButton}
          />
        </Flex>
      );
    }

    return (
      <Datepicker
        key={instance}
        {...DatepickerLocalizations}
        firstDayOfWeek={0}
        disabled={isDisabled}
        className={className ? `fluent-date ${className}` : 'fluent-date'}
        formatMonthDayYear={customDateFormatter}
        maxDate={maxDate}
        selectedDate={ selectedDate }
        onDateChange={onDateChange}
        input={input==null ? <></> : input}
        calendar={{
          // Northstar holds the calendar at a six-week month's height, leaving a blank row under five-week months.
          styles: { minHeight: 'auto' },
          header: {
            children: calendarChildren
          },
          ...(onClear && selectedDate ? {
            children: calendarWithClear(() => { onClear(); setInstance(n => n + 1); })
          } : {})
        }}
      />
    );
  }